/**
 * Custom hook for fetching initiatives with pagination, filtering, and search
 * @module hooks/use-initiatives
 */

import { useState, useEffect, useCallback } from 'react';
import { getInitiatives } from '@/lib/api/initiatives';
import { getCached, setCached, isFresh } from '@/lib/api/cache';
import type {
  Initiative,
  InitiativeQueryParams,
  InitiativeCategory,
  InitiativeStatus,
} from '@/types/initiative';
import type { PaginationMeta } from '@/types/common';

export interface UseInitiativesParams {
  initialPage?: number;
  initialPerPage?: number;
  initialCategory?: InitiativeCategory;
  initialStatus?: InitiativeStatus;
  initialType?: string;
  initialSearch?: string;
}

export interface UseInitiativesReturn {
  data: Initiative[];
  loading: boolean;
  error: string | null;
  meta: PaginationMeta | null;
  refetch: () => Promise<void>;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  setCategory: (category: InitiativeCategory | undefined) => void;
  setStatus: (status: InitiativeStatus | undefined) => void;
  setType: (type: string | undefined) => void;
  setSearch: (search: string) => void;
  currentParams: InitiativeQueryParams;
}

/**
 * Custom hook for fetching paginated, filtered, and searchable initiatives
 *
 * @example
 * const { data, loading, error, meta, setPage, setCategory, setSearch } = useInitiatives();
 * // Change page
 * setPage(2);
 * // Filter by category
 * setCategory('green');
 * // Search by title
 * setSearch('smart');
 */
export function useInitiatives(
  params: UseInitiativesParams = {}
): UseInitiativesReturn {
  const [data, setData] = useState<Initiative[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);

  const [currentParams, setCurrentParams] = useState<InitiativeQueryParams>({
    page: params.initialPage || 1,
    per_page: params.initialPerPage || 10,
    category: params.initialCategory,
    status: params.initialStatus,
    type: params.initialType,
    search: params.initialSearch,
  });

  const fetchInitiatives = useCallback(async () => {
    const cacheKey = `initiatives:${JSON.stringify(currentParams)}`;
    try {
      // Stale-while-revalidate: serve cached data immediately if present
      const cached = getCached<Initiative[]>(cacheKey);
      if (cached) setData(cached);

      // Skip network fetch if data is still fresh
      if (isFresh(cacheKey)) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      const response = await getInitiatives(currentParams);

      if (response.success && response.data) {
        setCached(cacheKey, response.data, 60_000);
        setData(response.data);
        setMeta(response.meta ?? null);
      } else {
        setError(response.message || 'Failed to load initiatives');
        if (!cached) {
          setData([]);
          setMeta(null);
        }
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'An unexpected error occurred'
      );
      const cached = getCached<Initiative[]>(cacheKey);
      if (!cached) {
        setData([]);
        setMeta(null);
      }
    } finally {
      setLoading(false);
    }
  }, [currentParams]);

  useEffect(() => {
    fetchInitiatives();
  }, [fetchInitiatives]);

  const setPage = useCallback((page: number) => {
    setCurrentParams((prev) => ({ ...prev, page }));
  }, []);

  const setPerPage = useCallback((perPage: number) => {
    setCurrentParams((prev) => ({ ...prev, per_page: perPage, page: 1 }));
  }, []);

  const setCategory = useCallback((category: InitiativeCategory | undefined) => {
    setCurrentParams((prev) => ({ ...prev, category, page: 1 }));
  }, []);

  const setStatus = useCallback((status: InitiativeStatus | undefined) => {
    setCurrentParams((prev) => ({ ...prev, status, page: 1 }));
  }, []);

  const setType = useCallback((type: string | undefined) => {
    setCurrentParams((prev) => ({ ...prev, type, page: 1 }));
  }, []);

  const setSearch = useCallback((search: string) => {
    setCurrentParams((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  return {
    data,
    loading,
    error,
    meta,
    refetch: fetchInitiatives,
    setPage,
    setPerPage,
    setCategory,
    setStatus,
    setType,
    setSearch,
    currentParams,
  };
}
